import { supabase } from '@/lib/supabase';
import { UserPreferences, UpdateUserPreferencesForm, ApiResponse } from '@/types/database';

export const preferencesApi = {
  // Get user preferences
  async getUserPreferences(): Promise<ApiResponse<UserPreferences>> {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch user preferences',
        success: false
      };
    }
  },

  // Update user preferences
  async updateUserPreferences(preferences: UpdateUserPreferencesForm): Promise<ApiResponse<UserPreferences>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_preferences')
        .update({
          story_length_preference: preferences.story_length_preference,
          audience_rating: preferences.audience_rating,
          preferred_genres: preferences.preferred_genres,
          illustration_style_preference: preferences.illustration_style_preference,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update user preferences',
        success: false
      };
    }
  },

  // Create default user preferences
  async createDefaultPreferences(): Promise<ApiResponse<UserPreferences>> {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .insert({
          story_length_preference: 'medium',
          audience_rating: 'child',
          preferred_genres: ['adventure'],
          illustration_style_preference: 'watercolor'
        })
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create default preferences',
        success: false
      };
    }
  },

  // Get or create user preferences
  async getOrCreatePreferences(): Promise<ApiResponse<UserPreferences>> {
    try {
      // Try to get existing preferences
      const { data: existingPrefs, error: fetchError } = await supabase
        .from('user_preferences')
        .select('*')
        .single();

      if (existingPrefs) {
        return {
          data: existingPrefs,
          success: true
        };
      }

      // If no preferences exist, create default ones
      return await this.createDefaultPreferences();
    } catch (error) {
      // If fetch failed, try to create default preferences
      return await this.createDefaultPreferences();
    }
  }
}; 